/**
 * Founder "Billing / Entitlement Override Control" (2026-09-07) —
 * Admin Console -> Customers/Tenants -> [Company] -> Billing & Entitlements.
 *
 * Platform-admin + MFA only (enforced server-side by
 * auth.require_platform_admin on every platform tenant billing_override
 * route -- this component has zero authority of its own; hiding/showing
 * it in the UI is not the security boundary).
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Clock, X, CreditCard } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const OVERRIDE_TYPES = [
  { value: "courtesy_free", label: "Courtesy free period", hint: "Billing waived, access continues." },
  { value: "discount", label: "Temporary discount", hint: "Bounded % or fixed discount; tier unchanged." },
  { value: "payment_pause", label: "Payment collection pause", hint: "Billing paused, access continues." },
  { value: "entitlement_only", label: "Entitlement-only override", hint: "Force access active despite a billing/provider issue." },
  { value: "manual_enterprise", label: "Manual / enterprise / comped", hint: "Negotiated relationship billed outside Stripe." },
];

const TYPE_LABELS = Object.fromEntries(OVERRIDE_TYPES.map((t) => [t.value, t.label]));

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

export default function BillingOverridePanel({ tenant }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [overrideType, setOverrideType] = useState("courtesy_free");
  const [reason, setReason] = useState("");
  const [durationDays, setDurationDays] = useState(30);
  const [isPermanent, setIsPermanent] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const [endReason, setEndReason] = useState("");
  const [extendDays, setExtendDays] = useState(7);
  const [extendReason, setExtendReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/platform/tenants/${tenant.id}/billing_overview`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load billing overview");
    } finally { setLoading(false); }
  }, [tenant.id]);
  useEffect(() => { load(); }, [load]);

  async function applyOverride() {
    setBusy(true); setError(null);
    try {
      const body = {
        override_type: overrideType,
        reason,
        is_permanent: overrideType === "manual_enterprise" ? isPermanent : false,
      };
      if (!body.is_permanent) body.duration_days = Number(durationDays);
      if (overrideType === "discount") body.discount_percent = Number(discountPercent);
      await api.post(`/platform/tenants/${tenant.id}/billing_override`, body);
      toast.success("Billing override applied — customer access updated, audit recorded.");
      setReason("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not apply override");
    } finally { setBusy(false); }
  }

  async function endOverride(id) {
    setBusy(true); setError(null);
    try {
      await api.post(`/platform/tenants/${tenant.id}/billing_override/${id}/end`, { reason: endReason || undefined });
      toast.success("Override ended — normal billing/entitlement restored.");
      setEndReason("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not end override");
    } finally { setBusy(false); }
  }

  async function extendOverride(id) {
    if (!extendReason || extendReason.trim().length < 10) {
      setError("A reason (min 10 characters) is required to extend an override.");
      return;
    }
    setBusy(true); setError(null);
    try {
      await api.post(`/platform/tenants/${tenant.id}/billing_override/${id}/modify`, {
        reason: extendReason, extend_days: Number(extendDays),
      });
      toast.success(`Override extended by ${extendDays} day(s) — audit recorded.`);
      setExtendReason("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not extend override");
    } finally { setBusy(false); }
  }

  async function cancelImmediate() {
    setBusy(true); setError(null);
    try {
      await api.post(`/platform/tenants/${tenant.id}/billing_cancel_immediate`);
      toast.success("Subscription canceled immediately.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not cancel subscription");
    } finally { setBusy(false); }
  }

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }
  if (!data) return null;

  const { tenant: t, plan_label, active_override, history, usage } = data;

  return (
    <section className="py-4 space-y-4" data-testid="tenant-billing-tab">
      <header>
        <div className="text-sm font-medium text-[var(--fop-text)] flex items-center gap-1.5">
          <CreditCard size={14} className="text-[var(--fop-primary)]" /> Billing &amp; Entitlements
        </div>
        <p className="text-xs text-[var(--fop-text-3)] mt-1 leading-5 max-w-prose">
          Stripe is the billing provider. ForgedOps remains the canonical entitlement
          authority — an active override here always takes precedence over Stripe-derived
          state, is fully audited, and expires automatically unless marked permanent.
        </p>
      </header>

      {/* Current commercial snapshot */}
      <div className="border border-[var(--fop-border)] rounded p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="billing-snapshot">
        <div><div className="text-[var(--fop-text-3)] uppercase tracking-wider text-[10px]">Plan</div><div className="text-[var(--fop-text)] mt-0.5">{plan_label || t.plan_code || "—"}{t.billing_interval ? ` · ${t.billing_interval}` : ""}</div></div>
        <div><div className="text-[var(--fop-text-3)] uppercase tracking-wider text-[10px]">Billing status</div><div className="text-[var(--fop-text)] mt-0.5 capitalize">{t.subscription_status}</div></div>
        <div><div className="text-[var(--fop-text-3)] uppercase tracking-wider text-[10px]">Access</div>
          <div className={`mt-0.5 font-medium ${active_override ? "text-emerald-400" : "text-[var(--fop-text)]"}`} data-testid="billing-effective-access">
            {active_override ? "Active via Founder override" : (t.subscription_status === "canceled" || t.subscription_status === "unpaid" ? "Blocked" : "Normal")}
          </div>
        </div>
        <div><div className="text-[var(--fop-text-3)] uppercase tracking-wider text-[10px]">Stripe customer</div><div className="text-[var(--fop-text)] mt-0.5 font-mono truncate">{t.stripe_customer_id || "—"}</div></div>
        <div><div className="text-[var(--fop-text-3)] uppercase tracking-wider text-[10px]">Renewal</div>
          <div className="text-[var(--fop-text)] mt-0.5" data-testid="billing-renewal-info">
            {t.subscription_current_period_end
              ? (t.subscription_cancel_at_period_end ? `Ends ${fmt(t.subscription_current_period_end)} (not renewing)` : `Renews ${fmt(t.subscription_current_period_end)}`)
              : "—"}
          </div>
        </div>
        <div><div className="text-[var(--fop-text-3)] uppercase tracking-wider text-[10px]">Stripe subscription</div><div className="text-[var(--fop-text)] mt-0.5 font-mono truncate">{t.stripe_subscription_id || "—"}</div></div>
        <div className="col-span-2 flex items-end justify-end">
          {t.stripe_subscription_id && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={busy}
                        className="text-red-400 border-red-500/30" data-testid="billing-cancel-immediate-button">
                  <X size={12} className="mr-1" /> Cancel subscription now (immediate)
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="billing-cancel-immediate-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Immediately cancel this subscription?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This bypasses the normal cancel-at-period-end policy and revokes
                    product access right away. This action is audited and cannot be
                    undone from this panel.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="billing-cancel-immediate-abort">Keep subscription</AlertDialogCancel>
                  <AlertDialogAction onClick={cancelImmediate} className="bg-red-600 hover:bg-red-700"
                                     data-testid="billing-cancel-immediate-confirm">
                    Cancel immediately
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {error ? (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="billing-override-error">
          {error}
        </div>
      ) : null}

      {active_override ? (
        <div className="border border-emerald-500/40 bg-emerald-500/5 rounded p-3 space-y-2" data-testid="billing-override-active-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-[var(--fop-text)] font-medium flex items-center gap-1.5">
              <Clock size={12} /> Courtesy Override Active — {TYPE_LABELS[active_override.override_type] || active_override.override_type}
            </div>
            <Button size="sm" variant="outline" onClick={() => endOverride(active_override.id)} disabled={busy}
                    data-testid="billing-override-end-button">
              <X size={12} className="mr-1" /> End override
            </Button>
          </div>
          <div className="text-xs text-[var(--fop-text-3)]">Reason: {active_override.reason}</div>
          <div className="text-xs text-[var(--fop-text-3)]">
            Expires: {active_override.is_permanent ? "Never (permanent)" : fmt(active_override.expires_at)}
          </div>
          <div className="text-xs text-[var(--fop-text-3)]">Applied by: {active_override.created_by_email}</div>
          {active_override.stripe_action_result && (
            <div className="text-xs text-[var(--fop-text-3)]">Stripe action: {active_override.stripe_action || "none"} — {active_override.stripe_action_result}</div>
          )}
          {active_override.last_modified_at && (
            <div className="text-xs text-[var(--fop-text-3)]">Last modified: {fmt(active_override.last_modified_at)} by {active_override.last_modified_by}</div>
          )}
          {!active_override.is_permanent && (
            <div className="flex items-center gap-2 pt-1 border-t border-[var(--fop-border)]/60 mt-1">
              <Label className="text-[11px] text-[var(--fop-text-3)] shrink-0">Extend by</Label>
              <Input type="number" min={1} value={extendDays} onChange={(e) => setExtendDays(e.target.value)}
                     className="w-16" data-testid="billing-override-extend-days-input" />
              <span className="text-[11px] text-[var(--fop-text-3)]">days</span>
              <Input placeholder="Reason for extending (required)"
                     value={extendReason} onChange={(e) => setExtendReason(e.target.value)}
                     className="flex-1" data-testid="billing-override-extend-reason-input" />
              <Button size="sm" variant="outline" onClick={() => extendOverride(active_override.id)} disabled={busy}
                      data-testid="billing-override-extend-button">
                Extend
              </Button>
            </div>
          )}
          <Input
            placeholder="Reason for ending early (optional)"
            value={endReason} onChange={(e) => setEndReason(e.target.value)}
            data-testid="billing-override-end-reason-input"
          />
        </div>
      ) : (
        <div className="border border-[var(--fop-border)] rounded p-3 space-y-2" data-testid="billing-override-apply-form">
          <div className="text-xs text-[var(--fop-text)] font-medium flex items-center gap-1.5">
            <ShieldAlert size={12} className="text-amber-400" /> Apply Billing Override
          </div>
          <select
            className="w-full text-xs bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded p-2 text-[var(--fop-text)]"
            value={overrideType} onChange={(e) => setOverrideType(e.target.value)}
            data-testid="billing-override-type-select"
          >
            {OVERRIDE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="text-[11px] text-[var(--fop-text-3)]">{OVERRIDE_TYPES.find((o) => o.value === overrideType)?.hint}</div>

          <textarea
            className="w-full text-xs bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded p-2"
            rows={2}
            placeholder="Required reason (min 10 characters) — e.g. Service recovery courtesy period per customer escalation #4471"
            value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="billing-override-reason-input"
          />

          {overrideType === "discount" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[var(--fop-text-3)]">Discount %</Label>
              <Input type="number" min={1} max={100} value={discountPercent}
                     onChange={(e) => setDiscountPercent(e.target.value)}
                     className="w-24" data-testid="billing-override-discount-input" />
            </div>
          )}

          {overrideType === "manual_enterprise" && (
            <label className="flex items-center gap-2 text-xs text-[var(--fop-text-3)]">
              <input type="checkbox" checked={isPermanent} onChange={(e) => setIsPermanent(e.target.checked)}
                     data-testid="billing-override-permanent-checkbox" />
              Permanent (no expiration) — explicit, separately authorized
            </label>
          )}

          {!isPermanent && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[var(--fop-text-3)]">Duration (days)</Label>
              <Input type="number" min={1} max={366} value={durationDays}
                     onChange={(e) => setDurationDays(e.target.value)}
                     className="w-24" data-testid="billing-override-duration-input" />
            </div>
          )}

          <Button size="sm" onClick={applyOverride} disabled={busy || reason.trim().length < 10}
                  data-testid="billing-override-apply-button">
            {busy ? "Applying…" : "Apply Billing Override"}
          </Button>
        </div>
      )}

      {/* Usage snapshot */}
      {usage && usage.length > 0 && (
        <div className="border border-[var(--fop-border)] rounded" data-testid="billing-usage-snapshot">
          <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-[var(--fop-text-3)] border-b border-[var(--fop-border)]">
            <span>Limit</span><span>Used</span><span>Status</span>
          </div>
          {usage.map((row) => (
            <div key={row.limit} className="grid grid-cols-3 gap-2 px-3 py-2 text-xs items-center border-b border-[var(--fop-border)] last:border-b-0">
              <span className="capitalize">{row.limit}</span>
              <span className="text-[var(--fop-text-3)]">{row.current}{row.cap != null && !row.unlimited ? ` / ${row.cap}` : ""}</span>
              <span className={row.status === "blocked" ? "text-red-400" : "text-[var(--fop-text-3)]"}>{row.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Audit history */}
      <div className="border border-[var(--fop-border)] rounded" data-testid="billing-override-history">
        <div className="grid grid-cols-4 gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-[var(--fop-text-3)] border-b border-[var(--fop-border)]">
          <span>Status</span><span>Type</span><span>Reason</span><span>Created</span>
        </div>
        {!history || history.length === 0 ? (
          <div className="px-3 py-4 text-xs text-[var(--fop-text-3)]">No billing overrides for this tenant.</div>
        ) : history.map((h) => (
          <div key={h.id} className="grid grid-cols-4 gap-2 px-3 py-2 text-xs items-center border-b border-[var(--fop-border)] last:border-b-0"
               data-testid={`billing-override-history-row-${h.id}`}>
            <span className={h.status === "active" ? "text-emerald-400" : h.status === "reconciliation_failed" ? "text-red-400" : "text-[var(--fop-text-3)]"}>{h.status}</span>
            <span className="truncate">{TYPE_LABELS[h.override_type] || h.override_type}</span>
            <span className="truncate text-[var(--fop-text-3)]" title={h.reason}>{h.reason}</span>
            <span className="text-[var(--fop-text-3)]">{fmt(h.created_at)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
